import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ChevronRight, Newspaper, ArrowLeft, Search, X, FileText, Trash2, ListChecks, CalendarClock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RESEARCH } from "@/constants/testIds";
import IdeasList from "@/components/IdeasList";
import SourceReportDialog from "@/components/SourceReportDialog";

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function RunsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  // Debounce user typing to avoid hammering the search endpoint
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const searching = debouncedQ.length >= 2;

  const { data: searchData, isFetching: searchLoading } = useQuery({
    queryKey: ["ideas-search", debouncedQ],
    queryFn: () => api.searchIdeas(debouncedQ, 80),
    enabled: searching,
    staleTime: 30000,
  });

  const { data: runs = [], isLoading: runsLoading } = useQuery({
    queryKey: ["past-runs"],
    queryFn: api.listResearch,
  });

  // Editor-driven deletion of stale / unusable runs. Using useMutation
  // (not a raw fetch) so the past-runs list re-queries and any
  // currently-open run detail closes automatically when its parent
  // row is deleted.
  const deleteRun = useMutation({
    mutationFn: (id) => api.deleteResearch(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["past-runs"] });
      if (id === selectedId) setSelectedId(null);
      toast.success("Run deleted");
    },
    onError: (e) => {
      toast.error(
        e?.response?.data?.detail || e.message || "Delete failed",
      );
    },
  });

  const handleDeleteRun = (e, id, prompt) => {
    e.stopPropagation();
    const preview = (prompt || "").slice(0, 80);
    if (
      !window.confirm(
        `Delete this past run?\n\n"${preview}"\n\n` +
          "Ideas from this run will be removed too. Articles already " +
          "written from this run stay in the Library.",
      )
    )
      return;
    deleteRun.mutate(id);
  };

  const { data: writers = [] } = useQuery({
    queryKey: ["writers"],
    queryFn: api.listWriters,
  });

  const { data: activeRun, isLoading: runLoading } = useQuery({
    queryKey: ["research", selectedId],
    queryFn: () => api.getResearch(selectedId),
    enabled: !!selectedId,
    refetchOnMount: true,
  });

  return (
    <div className="flex h-full min-w-0">
      {/* Left: runs list — on mobile, hidden when a run is selected */}
      <div
        className={[
          "md:w-96 md:shrink-0 md:border-r border-[var(--border)] bg-white overflow-y-auto",
          selectedId ? "hidden md:block" : "w-full md:block",
        ].join(" ")}
      >
        <div className="px-5 sm:px-6 py-5 border-b border-[var(--border)] sticky top-0 bg-white z-10">
          <div className="overline">Newsroom · Archive</div>
          <h2 className="font-serif-display text-2xl tracking-tight mt-1">
            Past runs
          </h2>
          <p className="text-xs text-[var(--ink-muted)] mt-1">
            Every research session is saved. Revisit and write from any idea.
          </p>

          {/* Search across all past ideas */}
          <div className="relative mt-4">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
            <input
              data-testid="ideas-search-input"
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search all past ideas…"
              className="w-full border border-[var(--border)] bg-[var(--bg)] pl-8 pr-8 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)]"
            />
            {searchQ && (
              <button
                onClick={() => setSearchQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Search results OR list of runs */}
        {searching ? (
          <>
            <div className="px-5 sm:px-6 py-3 border-b border-[var(--border)] text-[11px] text-[var(--ink-muted)] font-mono-display uppercase tracking-wider flex items-center gap-2">
              {searchLoading ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Searching…
                </>
              ) : (
                <>
                  Search · {searchData?.count ?? 0} match
                  {(searchData?.count ?? 0) === 1 ? "" : "es"}
                </>
              )}
            </div>
            {!searchLoading && (searchData?.count ?? 0) === 0 && (
              <div
                data-testid="ideas-search-empty"
                className="p-6 text-sm text-[var(--ink-muted)] italic"
              >
                No past ideas match &ldquo;{debouncedQ}&rdquo;. Try broader
                keywords.
              </div>
            )}
            {(searchData?.results || []).map((r) => (
              <button
                key={r.idea_id}
                data-testid={`ideas-search-result-${r.idea_id}`}
                onClick={() => {
                  if (r.article_id) {
                    window.location.assign(`/article/${r.article_id}`);
                  } else if (r.research_id) {
                    setSelectedId(r.research_id);
                    setSearchQ("");
                  }
                }}
                className="w-full text-left px-5 sm:px-6 py-3 border-b border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                <div className="flex items-start gap-2">
                  {r.article_id ? (
                    <FileText className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
                  ) : (
                    <Newspaper className="w-3.5 h-3.5 text-[var(--ink-muted)] mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--ink)] leading-snug">
                      {r.headline_draft}
                    </div>
                    {r.angle && (
                      <div className="text-xs text-[var(--ink-muted)] mt-0.5 line-clamp-2">
                        {r.angle}
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-[var(--ink-muted)] font-mono-display flex-wrap">
                      <span>{relativeTime(r.created_at)}</span>
                      {r.category && <span>· {r.category}</span>}
                      {r.article_id ? (
                        <span className="text-emerald-700">· written</span>
                      ) : (
                        <span>· idea only</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--ink-muted)] shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </>
        ) : (
          <>
            {runsLoading && (
              <div className="p-6 text-sm text-[var(--ink-muted)]">Loading…</div>
            )}

            {!runsLoading && runs.length === 0 && (
              <div className="p-6 text-sm text-[var(--ink-muted)]">
                No runs yet. Run your first research from the Research page.
              </div>
            )}

            {runs.map((r) => {
              const isActive = r.id === selectedId;
              const writtenCount = (r.ideas || []).filter(
                (i) => i.article_id
              ).length;
              const total = (r.ideas || []).length;
              const isDeleting =
                deleteRun.isPending && deleteRun.variables === r.id;
              return (
                <div
                  key={r.id}
                  className={`group relative border-b border-[var(--border)] ${
                    isActive ? "bg-[var(--secondary)]" : ""
                  } ${isDeleting ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <button
                    data-testid={`${RESEARCH.pastRunItem}-${r.id}`}
                    onClick={() => setSelectedId(r.id)}
                    className="w-full text-left px-5 sm:px-6 py-4 pr-12 hover:bg-[var(--secondary)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Newspaper className="w-3 h-3 text-[var(--accent)]" />
                          <span className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                            {relativeTime(r.created_at)}
                          </span>
                          {r.status === "pending" && (
                            <span className="text-[10px] uppercase tracking-wider text-amber-700 font-mono-display flex items-center gap-1">
                              <Loader2 className="w-2.5 h-2.5 animate-spin" />
                              running
                            </span>
                          )}
                          {r.status === "error" && (
                            <span className="text-[10px] uppercase tracking-wider text-red-700 font-mono-display">
                              error
                            </span>
                          )}
                          {r.trigger === "scheduled" && (
                            <span
                              data-testid={`run-scheduled-badge-${r.id}`}
                              title={
                                r.schedule_name
                                  ? `Produced automatically by the "${r.schedule_name}" schedule`
                                  : "Produced automatically by a research schedule"
                              }
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display px-1.5 py-0.5 bg-neutral-100"
                            >
                              <CalendarClock className="w-2.5 h-2.5" />
                              {r.schedule_name || "scheduled"}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--ink)] line-clamp-2 leading-snug">
                          {r.prompt}
                        </p>
                        <div className="mt-2 flex items-center gap-3 text-[11px] text-[var(--ink-muted)] font-mono-display flex-wrap">
                          <span>{total} ideas</span>
                          {writtenCount > 0 && (
                            <span className="text-emerald-700">
                              · {writtenCount} written
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[var(--ink-muted)] shrink-0 mt-1" />
                    </div>
                  </button>
                  {/* Trash icon — always visible on mobile (no hover on
                      touch), fades in on hover on desktop so it doesn't
                      clutter the list. Uses stopPropagation so tapping
                      it doesn't also open the run. */}
                  <button
                    type="button"
                    data-testid={`delete-run-${r.id}`}
                    onClick={(e) => handleDeleteRun(e, r.id, r.prompt)}
                    disabled={isDeleting}
                    aria-label="Delete this run"
                    title="Delete this run"
                    className="absolute top-3 right-3 p-1.5 text-[var(--ink-muted)] hover:text-red-600 hover:bg-red-50 md:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  >
                    {isDeleting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Right: run detail — on mobile shown only when selected */}
      <div
        className={[
          "flex-1 overflow-y-auto min-w-0",
          selectedId ? "block" : "hidden md:flex md:items-center md:justify-center",
        ].join(" ")}
      >
        {!selectedId && (
          <div className="text-center px-8 hidden md:block">
            <div className="font-serif-display text-2xl mb-2">
              Pick a run to review
            </div>
            <div className="text-sm text-[var(--ink-muted)]">
              Every past research session is saved with its full set of ideas.
            </div>
          </div>
        )}

        {selectedId && runLoading && (
          <div className="p-10 flex items-center gap-2 text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading run…
          </div>
        )}

        {selectedId && activeRun && (
          <div className="px-4 sm:px-10 py-6 sm:py-8">
            <button
              onClick={() => setSelectedId(null)}
              className="md:hidden inline-flex items-center gap-1.5 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to runs
            </button>
            <div className="mb-6">
              <div className="overline">
                {new Date(activeRun.created_at).toLocaleString()} · {activeRun.today}
              </div>
              <h2 className="font-serif-display text-2xl sm:text-3xl tracking-tight mt-1 break-words">
                {activeRun.prompt.length > 90
                  ? activeRun.prompt.slice(0, 90) + "…"
                  : activeRun.prompt}
              </h2>
              <div className="mt-3">
                <button
                  type="button"
                  data-testid="open-source-report"
                  onClick={() => setReportOpen(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-mono-display uppercase tracking-wider text-[var(--accent)] hover:underline"
                  title="See exactly what each source contributed and any fetch errors"
                >
                  <ListChecks className="w-3.5 h-3.5" />
                  View fetch report
                </button>
              </div>
            </div>
            <IdeasList research={activeRun} writers={writers} />
          </div>
        )}
      </div>
      <SourceReportDialog
        researchId={selectedId}
        open={reportOpen}
        onOpenChange={setReportOpen}
      />
    </div>
  );
}
